import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { _T, C } from '../shared/theme';

const BASE = process.env.REACT_APP_API_URL || '/api';

const PERSONAL_PLANS = [
  {
    id: 'athlete',
    icon: '🏃',
    name: 'Athlete',
    price: { nl: '€3,95 / maand', en: '€3.95 / month' },
    color: '#2A9E82',
    recommended: true,
    tagline: { nl: '~€0,09 per sessie · ideaal voor dagelijks gebruik', en: '~€0.09 per session · ideal for daily use' },
    features: {
      nl: ['45 credits/maand', 'Alle oefeningen & programma\'s', 'Multiplayer games', 'Geavanceerde statistieken', 'Premium thema\'s', 'Data export'],
      en: ['45 credits/month', 'All exercises & programs', 'Multiplayer games', 'Advanced statistics', 'Premium themes', 'Data export'],
    },
  },
  {
    id: 'starter',
    icon: '🌱',
    name: 'Starter',
    price: { nl: '€1,00 / maand', en: '€1.00 / month' },
    color: '#5B9ED0',
    tagline: { nl: 'Minder dan een kop koffie', en: 'Less than a cup of coffee' },
    features: {
      nl: ['8 credits/maand', 'Alle games', 'Basis oefeningen', 'Push herinneringen'],
      en: ['8 credits/month', 'All games', 'Basic exercises', 'Push reminders'],
    },
  },
  {
    id: 'sensei',
    icon: '🥋',
    name: 'Sensei',
    badge: { nl: 'Meest gekozen', en: 'Most popular' },
    price: { nl: '€7,95 / maand', en: '€7.95 / month' },
    color: '#D4873A',
    tagline: { nl: 'Complete dagelijkse routine', en: 'Complete daily routine' },
    features: {
      nl: ['75 credits/maand', 'Sense Lifting Coach (onbeperkt)', 'Lab programma\'s', 'Audio begeleiding', 'Adaptieve spelmoeilijkheid'],
      en: ['75 credits/month', 'Sense Lifting Coach (unlimited)', 'Lab programs', 'Audio guidance', 'Adaptive game difficulty'],
    },
  },
  {
    id: 'professional',
    icon: '💎',
    name: 'Professional',
    price: { nl: '€9,95 / maand', en: '€9.95 / month' },
    color: '#C8A84B',
    tagline: { nl: 'Onbeperkt alles', en: 'Unlimited everything' },
    features: {
      nl: ['Onbeperkte credits', 'Alles van Sensei', 'Prioriteit support', 'Vroege toegang nieuwe functies'],
      en: ['Unlimited credits', 'Everything in Sensei', 'Priority support', 'Early access to new features'],
    },
  },
];

/**
 * TrialEndModal
 * variant='last_day' — shown on day 7 (last free day), closeable
 * variant='urgent'   — shown on day 8+ (trial ended), full-screen lockout, not closeable
 *
 * daysLeftUntilDeletion — number of days until account is deleted (day 10)
 */
export default function TrialEndModal({ lang, variant, daysLeftUntilDeletion = 0, onClose, onLicenseChange, onShowAllPlans }) {
  const nl = lang === 'nl';
  const isLastDay = variant === 'last_day';
  const [selectedPlan, setSelectedPlan] = useState('athlete');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const stats = useMemo(() => {
    try {
      const exHistory  = JSON.parse(localStorage.getItem('sl_ex_history')  || '[]');
      const sleepLog   = JSON.parse(localStorage.getItem('sl_sleep_log')   || '[]');
      const moodLog    = JSON.parse(localStorage.getItem('sl_mood_log')    || '[]');
      const gameLevels = JSON.parse(localStorage.getItem('sl_game_levels') || '{}');
      const myPosts    = JSON.parse(localStorage.getItem('sl_my_posts')    || '[]');
      const friends    = JSON.parse(localStorage.getItem('sl_friends')     || '[]');
      return {
        exercises: exHistory.length,
        sleepLogs: sleepLog.length,
        moodLogs:  moodLog.length,
        games:     Object.values(gameLevels).filter(v => v > 0).length,
        posts:     myPosts.length,
        friends:   Array.isArray(friends) ? friends.length : 0,
      };
    } catch { return { exercises: 0, sleepLogs: 0, moodLogs: 0, games: 0, posts: 0, friends: 0 }; }
  }, []);

  const hasAnyStats = stats.exercises + stats.sleepLogs + stats.moodLogs + stats.games + stats.posts + stats.friends > 0;

  const handleUpgrade = async () => {
    const uid = localStorage.getItem('sl_user_id');
    if (!uid) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BASE}/stripe/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planId: selectedPlan, uid }),
      });
      const data = await res.json();
      if (data.url) { window.location.href = data.url; return; }
      setError(nl ? 'Er ging iets mis. Probeer opnieuw.' : 'Something went wrong. Please try again.');
    } catch {
      setError(nl ? 'Verbindingsfout. Probeer opnieuw.' : 'Connection error. Please try again.');
    }
    setLoading(false);
  };

  const plan = PERSONAL_PLANS.find(p => p.id === selectedPlan);

  const content = (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9998,
      background: isLastDay ? 'rgba(5,8,18,0.82)' : 'rgba(5,8,18,0.98)',
      backdropFilter: 'blur(20px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16, overflowY: 'auto',
    }}>
      <div style={{
        width: '100%', maxWidth: 500,
        background: _T.bg2,
        borderRadius: 24,
        border: `1px solid ${_T.cardBorderTop}`,
        padding: '28px 22px 32px',
        position: 'relative',
        maxHeight: '92vh', overflowY: 'auto',
      }}>

        {/* Close button — only for last_day variant */}
        {isLastDay && onClose && (
          <button onClick={onClose} style={{
            position: 'absolute', top: 14, right: 14,
            background: 'transparent', border: 'none',
            color: C.silver, fontSize: 22, cursor: 'pointer',
            padding: 4, lineHeight: 1, zIndex: 1,
          }}>✕</button>
        )}

        {/* Urgency banner */}
        {!isLastDay && (
          <div style={{
            background: 'rgba(220,50,50,0.12)',
            border: '1px solid rgba(220,50,50,0.4)',
            borderRadius: 12, padding: '10px 14px',
            marginBottom: 20,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <div>
              <div style={{ color: '#ff5555', fontSize: 13, fontWeight: 700 }}>
                {daysLeftUntilDeletion > 0
                  ? (nl
                    ? `Nog ${daysLeftUntilDeletion} dag${daysLeftUntilDeletion !== 1 ? 'en' : ''} voor je account wordt verwijderd`
                    : `${daysLeftUntilDeletion} day${daysLeftUntilDeletion !== 1 ? 's' : ''} until your account is deleted`)
                  : (nl ? 'Je account wordt binnenkort verwijderd' : 'Your account will be deleted soon')}
              </div>
              <div style={{ color: C.silver, fontSize: 11, marginTop: 2 }}>
                {nl ? 'Upgrade nu om je voortgang permanent te bewaren.' : 'Upgrade now to permanently save your progress.'}
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <div style={{ fontSize: 52, marginBottom: 10 }}>{isLastDay ? '🏆' : '🔓'}</div>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, color: '#fff', lineHeight: 1.3, marginBottom: 10 }}>
            {isLastDay
              ? (nl ? 'Laatste dag van je gratis periode' : 'Last day of your free trial')
              : (nl ? 'Je gratis periode is voorbij' : 'Your free trial has ended')}
          </div>
          <div style={{ color: '#8a9bb0', fontSize: 13, lineHeight: 1.7, maxWidth: 360, margin: '0 auto' }}>
            {isLastDay
              ? (nl
                ? 'Je hebt 7 dagen Sense Lifting ontdekt. Vandaag is de laatste dag van je gratis periode. Zet je journey voort!'
                : 'You\'ve explored Sense Lifting for 7 days. Today is your last free day. Continue your journey!')
              : (nl
                ? 'Jouw 7 dagen gratis Sense Lifting zijn op. Kies een plan om verder te gaan — je voortgang wacht op je.'
                : 'Your 7 free days of Sense Lifting are up. Choose a plan to continue — your progress is waiting.')}
          </div>
        </div>

        {/* Stats grid — only when user has built something */}
        {hasAnyStats && (
          <div style={{ background: _T.bg4, borderRadius: 16, padding: '14px 16px', marginBottom: 22 }}>
            <div style={{ fontSize: 10, color: '#8a9bb0', letterSpacing: 2, fontWeight: 600, marginBottom: 12 }}>
              📊 {nl ? 'WAT JE HEBT OPGEBOUWD' : 'WHAT YOU\'VE BUILT'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {[
                { icon: '🏋️', label: nl ? 'Oefeningen' : 'Exercises', val: stats.exercises },
                { icon: '🎮', label: nl ? 'Game sessies' : 'Game sessions', val: stats.games },
                { icon: '😴', label: nl ? 'Slaaplogs' : 'Sleep logs', val: stats.sleepLogs },
                { icon: '😊', label: nl ? 'Stemmingen' : 'Mood logs', val: stats.moodLogs },
                { icon: '📝', label: nl ? 'Posts' : 'Posts', val: stats.posts },
                { icon: '👥', label: nl ? 'Vrienden' : 'Friends', val: stats.friends },
              ].map(s => (
                <div key={s.label} style={{ textAlign: 'center', background: _T.bg2, borderRadius: 10, padding: '9px 4px' }}>
                  <div style={{ fontSize: 17 }}>{s.icon}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>{s.val}</div>
                  <div style={{ fontSize: 9, color: '#8a9bb0', marginTop: 2, lineHeight: 1.2 }}>{s.label}</div>
                </div>
              ))}
            </div>
            {!isLastDay && (
              <div style={{ marginTop: 10, fontSize: 11, color: C.silver, textAlign: 'center' }}>
                {nl ? '⚠️ Al deze voortgang gaat verloren als je niet upgradet.' : '⚠️ All this progress will be lost if you don\'t upgrade.'}
              </div>
            )}
          </div>
        )}

        {/* Plan selector */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, color: _T.accent, letterSpacing: 2, fontWeight: 600, marginBottom: 12 }}>
            💳 {nl ? 'KIES JE PLAN' : 'CHOOSE YOUR PLAN'}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {PERSONAL_PLANS.map(p => {
              const isSel = selectedPlan === p.id;
              return (
                <div key={p.id} onClick={() => setSelectedPlan(p.id)} style={{
                  border: `2px solid ${isSel ? p.color : _T.cardBorderBot}`,
                  borderRadius: 14, padding: '11px 13px',
                  cursor: 'pointer',
                  background: isSel ? `${p.color}12` : _T.bg4,
                  transition: 'all 0.15s',
                  position: 'relative',
                }}>
                  {p.recommended && (
                    <div style={{
                      position: 'absolute', top: -9, left: 14,
                      background: p.color, color: '#fff',
                      fontSize: 9, fontWeight: 700,
                      padding: '2px 9px', borderRadius: 6, letterSpacing: 1,
                    }}>{nl ? '⭐ AANBEVOLEN' : '⭐ RECOMMENDED'}</div>
                  )}
                  {p.badge && !p.recommended && (
                    <div style={{
                      position: 'absolute', top: -9, left: 14,
                      background: p.color, color: '#fff',
                      fontSize: 9, fontWeight: 700,
                      padding: '2px 9px', borderRadius: 6, letterSpacing: 1,
                    }}>{p.badge[nl ? 'nl' : 'en'].toUpperCase()}</div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: '50%',
                      background: `${p.color}20`, border: `2px solid ${p.color}60`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 17, flexShrink: 0,
                    }}>{p.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>{p.name}</span>
                        <span style={{ color: p.color, fontSize: 14, fontWeight: 800 }}>{p.price[nl ? 'nl' : 'en']}</span>
                      </div>
                      <div style={{ color: '#6a7b8e', fontSize: 10, marginTop: 1 }}>{p.tagline[nl ? 'nl' : 'en']}</div>
                      {isSel && (
                        <div style={{ marginTop: 7, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {p.features[nl ? 'nl' : 'en'].map(f => (
                            <span key={f} style={{
                              fontSize: 10, color: '#8a9bb0',
                              background: `${p.color}15`,
                              borderRadius: 6, padding: '2px 7px',
                            }}>✓ {f}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{
                      width: 18, height: 18, borderRadius: '50%',
                      border: `2px solid ${isSel ? p.color : '#4a5568'}`,
                      background: isSel ? p.color : 'transparent',
                      flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {isSel && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {error && (
          <div style={{ color: '#ff5555', fontSize: 12, textAlign: 'center', marginBottom: 10 }}>{error}</div>
        )}

        {/* CTA */}
        <button onClick={handleUpgrade} disabled={loading} style={{
          width: '100%',
          background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`,
          border: 'none', borderRadius: 16,
          padding: '15px 0',
          color: '#0a1220', fontSize: 16, fontWeight: 800,
          cursor: 'pointer', letterSpacing: 0.3,
          marginBottom: 10,
          opacity: loading ? 0.7 : 1,
        }}>
          {loading
            ? (nl ? 'Even geduld...' : 'Please wait...')
            : (nl ? `Upgrade naar ${plan?.name} →` : `Upgrade to ${plan?.name} →`)}
        </button>

        <div style={{ textAlign: 'center', color: '#4a5568', fontSize: 11, marginBottom: isLastDay ? 12 : 0 }}>
          {nl ? 'Maandelijks opzegbaar · geen verborgen kosten · direct actief' : 'Cancel anytime · no hidden fees · active immediately'}
        </div>

        {isLastDay && onClose && (
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <button onClick={onClose} style={{
              background: 'transparent', border: 'none',
              color: '#4a5568', fontSize: 12,
              cursor: 'pointer', textDecoration: 'underline',
            }}>
              {nl ? 'Misschien later (nog 1 dag gratis)' : 'Maybe later (1 more free day)'}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}
